import React, { useEffect, useState } from 'react';
import ModalWrapper from '../ModalWrapper';
import { Alert } from '@mui/material';
import { fetchEnvironmentList } from '../../utils/api/environment_apis';
import { getTestGroupSummary } from '../../utils/api/testgroup_apis';
import { createSchedule, getSchedule, updateSchedule } from '../../utils/api/schedule_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { LaunchType, ScheduleCadence, ScheduleStatus } from '../../enums/ScheduleEnums';
import AIModelRuntimeSelector from '../Select/llmModelSelector/AIModelRuntimeSelector';
import { AI_FEATURE_KEYS } from '../../enums/AIModelEnums';
import { useSelector } from 'react-redux';
import { userCanSelectAiModel } from '../../utils/commonfunctions';
import { CheckboxComponent, CommonFooter, InputBox, Selectbox } from '../FormComponent';
import {
  describeFirstScheduledRun,
  formatScheduleTimezoneLabel,
} from '../../utils/scheduleDisplay';
import moment from 'moment';
import scheduleStyles from '../../styles/createTestLaunch/createScheduleModal.module.scss';
import typeCardStyles from '../../styles/createTestLaunch/launchTypeCards.module.scss';
import {
  LaunchTypeCheckIcon,
  LaunchTypeBanIcon,
} from './LaunchTypeIcons';
import {
  TYPE_OPTIONS,
  includedCaseCount,
} from './createTestLaunchShared';

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** Calendar day YYYY-MM-DD in an IANA timezone (schedule TZ, not browser-local). */
function calendarDayInZone(value, timeZone) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(value));
  } catch {
    return moment(value).format('YYYY-MM-DD');
  }
}

/** Local calendar day as YYYY-MM-DD for native date `min` attributes. */
const todayDateString = () => moment().format('YYYY-MM-DD');
const tomorrowDateString = () => moment().add(1, 'day').format('YYYY-MM-DD');
const laterDateString = (a, b) => (!a ? b : !b ? a : a > b ? a : b);

const CADENCE_OPTIONS = [
  { value: ScheduleCadence.DAILY, label: 'Daily' },
  { value: ScheduleCadence.WEEKLY, label: 'Weekly' },
  { value: ScheduleCadence.MONTHLY, label: 'Monthly' },
];

const WEEKDAY_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
];

const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => {
  const day = index + 1;
  return { value: String(day), label: String(day) };
});

function clampMonthDay(value) {
  const n = Number.parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(n)) {
    return String(moment().date());
  }
  return String(Math.min(31, Math.max(1, n)));
}

function defaultScheduleName(group) {
  const datePart = moment().format('YYYY-MM-DD');
  const base = (group?.name || 'Schedule').trim();
  return `${base} — ${datePart}`;
}

function formatRunTimeForInput(runTime) {
  if (!runTime) return '09:00';
  const s = String(runTime);
  const [hours, minutes] = s.split(':');
  if (hours !== undefined && minutes !== undefined) {
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  }
  return s.slice(0, 5);
}

function recurrenceDayFromSchedule(schedule, cadence) {
  if (cadence === ScheduleCadence.WEEKLY) {
    const wd = Number(schedule?.recurrence_day);
    return Number.isFinite(wd) && wd >= 1 && wd <= 7
      ? String(wd)
      : String(moment().isoWeekday());
  }
  if (cadence === ScheduleCadence.MONTHLY) {
    return clampMonthDay(schedule?.recurrence_day);
  }
  return '';
}

function CreateScheduleModal({
  open,
  onClose,
  onCreated,
  group,
  projectId,
  prefill = null,
  editScheduleId = null,
}) {
  const { user } = useSelector((state) => state.auth);
  const canSelectAiModel = userCanSelectAiModel(user, projectId);
  const isTrial = user?.plan_name === 'trial';
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [fetchingSchedule, setFetchingSchedule] = useState(false);
  const [modalError, setModalError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [environments, setEnvironments] = useState([]);
  const [fetchingEnvironments, setFetchingEnvironments] = useState(false);
  const [fetchingTestScope, setFetchingTestScope] = useState(false);
  const [testScope, setTestScope] = useState({ total: 0, automated: 0, manual: 0 });
  const [aiModelSelection, setAiModelSelection] = useState({ model: null, meta: {} });
  const [aiModelInitial, setAiModelInitial] = useState(null);
  const [aiSelectorKey, setAiSelectorKey] = useState(0);
  const [alreadyFiredToday, setAlreadyFiredToday] = useState(false);
  const [editingExpired, setEditingExpired] = useState(false);

  const [form, setForm] = useState({
    name: '',
    launchType: LaunchType.COMPLETE,
    environmentId: '',
    cadence: ScheduleCadence.DAILY,
    runTime: '09:00',
    timezone: browserTimezone(),
    recurrenceDay: String(moment().date()),
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().add(30, 'days').format('YYYY-MM-DD'),
    runOnceNow: false,
    isSequential: false,
    continueOnFailure: false,
  });

  const needsAiModel = form.launchType !== LaunchType.MANUAL && (testScope.automated || 0) > 0;
  const isEditMode = Boolean(editScheduleId);

  useEffect(() => {
    if (!open) return;
    setModalError('');
    setFieldErrors({});
    setAlreadyFiredToday(false);
    setEditingExpired(false);

    const loadScopeAndEnvs = () => {
      setFetchingEnvironments(true);
      fetchEnvironmentList()
        .then((res) => {
          if (res.status === STATUS.SUCCESS) {
            setEnvironments(res.data || []);
          }
        })
        .finally(() => setFetchingEnvironments(false));

      if (group?.id) {
        setFetchingTestScope(true);
        getTestGroupSummary({ testGroupId: group.id })
          .then((res) => {
            if (res.status === STATUS.SUCCESS) {
              setTestScope(res.data || { total: 0, automated: 0, manual: 0 });
            }
          })
          .finally(() => setFetchingTestScope(false));
      }
    };

    if (isEditMode) {
      setFetchingSchedule(true);
      getSchedule({ scheduleId: editScheduleId })
        .then((res) => {
          if (res.status !== STATUS.SUCCESS) {
            setModalError(res.error || 'Failed to load schedule');
            return;
          }
          const data = res.data || {};
          const schedule = data.schedule || data;
          const fireHistory = data.fireHistory || [];
          const cadence = schedule.cadence || ScheduleCadence.DAILY;
          setEditingExpired(schedule.status === ScheduleStatus.EXPIRED);
          const scheduleTz = schedule.timezone || browserTimezone();
          const todayInScheduleTz = calendarDayInZone(new Date(), scheduleTz);
          setAlreadyFiredToday(
            fireHistory.some(
              (row) => row.scheduled_fire_time
                && calendarDayInZone(row.scheduled_fire_time, scheduleTz) === todayInScheduleTz
            )
          );
          setForm({
            name: schedule.name || '',
            launchType: schedule.launch_type || LaunchType.COMPLETE,
            environmentId: schedule.environment_id || '',
            cadence,
            runTime: formatRunTimeForInput(schedule.run_time),
            timezone: schedule.timezone || browserTimezone(),
            recurrenceDay: recurrenceDayFromSchedule(schedule, cadence),
            startDate: schedule.start_date || moment().format('YYYY-MM-DD'),
            endDate: schedule.end_date || moment().add(30, 'days').format('YYYY-MM-DD'),
            runOnceNow: false,
            isSequential: Boolean(schedule.is_sequential),
            continueOnFailure: schedule.configuration?.continueOnFailure === true,
          });
          const llm = schedule.configuration?.llmModelId;
          if (llm && typeof llm === 'object' && llm.provider && llm.model_key) {
            const model = { ...llm };
            setAiModelInitial(model);
            setAiModelSelection({ model, meta: {} });
          } else {
            setAiModelInitial(null);
            setAiModelSelection({ model: null, meta: { serverResolve: true } });
          }
          setAiSelectorKey((k) => k + 1);
        })
        .finally(() => setFetchingSchedule(false));
      loadScopeAndEnvs();
      return;
    }

    const launchType = prefill?.launchType || LaunchType.COMPLETE;
    setForm({
      name: prefill?.scheduleName || defaultScheduleName(group),
      launchType,
      environmentId: prefill?.environmentId || '',
      cadence: ScheduleCadence.DAILY,
      runTime: '09:00',
      timezone: browserTimezone(),
      recurrenceDay: String(moment().date()),
      startDate: moment().format('YYYY-MM-DD'),
      endDate: moment().add(30, 'days').format('YYYY-MM-DD'),
      runOnceNow: false,
      isSequential: false,
      continueOnFailure: false,
    });
    setAiModelSelection(prefill?.aiModelSelection || { model: null, meta: {} });
    setAiModelInitial(prefill?.aiModelSelection?.model || null);
    setAiSelectorKey((k) => k + 1);
    loadScopeAndEnvs();
  }, [open, group?.id, group?.name, prefill, editScheduleId, isEditMode]);

  const updateField = (key, value) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'isSequential' && value) {
        next.continueOnFailure = false;
      }
      if (key === 'cadence') {
        if (value === ScheduleCadence.MONTHLY) {
          next.recurrenceDay = clampMonthDay(prev.recurrenceDay);
        } else if (value === ScheduleCadence.WEEKLY) {
          const wd = Number.parseInt(String(prev.recurrenceDay), 10);
          next.recurrenceDay = Number.isFinite(wd) && wd >= 1 && wd <= 7
            ? String(wd)
            : String(moment().isoWeekday());
        } else {
          next.recurrenceDay = '';
        }
      }
      if (key === 'recurrenceDay' && prev.cadence === ScheduleCadence.MONTHLY) {
        next.recurrenceDay = clampMonthDay(value);
      }
      if (key === 'startDate' && value) {
        const minEnd = laterDateString(tomorrowDateString(), value);
        if (!next.endDate || next.endDate < minEnd) {
          next.endDate = minEnd;
        }
      }
      return next;
    });
  };

  const handleLaunchTypeSelect = (value, disabled) => {
    if (disabled) return;
    updateField('launchType', value);
    if (value === LaunchType.MANUAL) {
      updateField('isSequential', false);
      updateField('continueOnFailure', false);
    }
  };

  const buildLlmConfig = () => {
    if (aiModelSelection.meta?.serverResolve || aiModelSelection.meta?.isAuto) {
      return 'auto';
    }
    if (aiModelSelection.model?.provider && aiModelSelection.model?.model_key) {
      return JSON.stringify({
        provider: aiModelSelection.model.provider,
        model_key: aiModelSelection.model.model_key,
        ...(aiModelSelection.model.aiApiKeyId ? { aiApiKeyId: aiModelSelection.model.aiApiKeyId } : {}),
      });
    }
    return null;
  };

  const handleSubmit = async () => {
    setModalError('');
    const nextFieldErrors = {};

    if (!group?.id) {
      setModalError('Test group is required.');
      return;
    }
    if (!form.name?.trim()) {
      nextFieldErrors.name = 'Schedule name is required.';
    } else if (form.name.trim().length > 255) {
      nextFieldErrors.name = 'Schedule name cannot exceed 255 characters.';
    }
    if (!form.environmentId) {
      nextFieldErrors.environmentId = 'Please select an Environment';
    }
    const llmRef = buildLlmConfig();
    if (needsAiModel && !llmRef) {
      nextFieldErrors.aiModel = 'AI Model is required';
    }
    if (!form.runTime) {
      nextFieldErrors.runTime = 'Run time is required.';
    }
    if (!form.endDate) {
      nextFieldErrors.endDate = 'Set an end date before saving this schedule.';
    }

    const today = todayDateString();
    const minAllowedEnd = tomorrowDateString();
    if (!isEditMode && form.startDate && form.startDate < today) {
      nextFieldErrors.startDate = 'Start date cannot be in the past.';
    }
    if (form.endDate && form.endDate < minAllowedEnd) {
      nextFieldErrors.endDate = 'End date must be at least tomorrow.';
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      nextFieldErrors.endDate = 'End date must be on or after the start date.';
    }

    let recurrenceDay = null;
    if (form.cadence === ScheduleCadence.WEEKLY || form.cadence === ScheduleCadence.MONTHLY) {
      const day = Number(form.recurrenceDay);
      const max = form.cadence === ScheduleCadence.WEEKLY ? 7 : 31;
      if (!Number.isInteger(day) || day < 1 || day > max) {
        nextFieldErrors.recurrenceDay = form.cadence === ScheduleCadence.WEEKLY
          ? 'Please select a weekday.'
          : 'Enter a valid day of month (1–31).';
      } else {
        recurrenceDay = day;
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }
    setFieldErrors({});

    let llmModelId = llmRef;
    if (llmRef && llmRef !== 'auto') {
      try {
        llmModelId = JSON.parse(llmRef);
      } catch {
        llmModelId = llmRef;
      }
    }

    const runTime = form.runTime.length === 5 ? `${form.runTime}:00` : form.runTime;
    const timezone = form.timezone || browserTimezone();

    const schedulePayload = {
      name: form.name.trim(),
      launchType: form.launchType,
      environmentId: form.environmentId,
      cadence: form.cadence,
      runTime,
      timezone,
      recurrenceDay,
      startDate: form.startDate,
      endDate: form.endDate,
      isSequential: Boolean(form.isSequential),
      config: {
        continueOnFailure: Boolean(form.continueOnFailure),
        llmModelId: llmModelId === 'auto' ? null : llmModelId,
      },
      runOnceNow: !isEditMode && Boolean(form.runOnceNow),
    };

    setLoading(true);
    try {
      if (isEditMode) {
        const response = await updateSchedule({
          scheduleId: editScheduleId,
          ...schedulePayload,
        });

        if (response.status !== STATUS.SUCCESS) {
          const errMsg = response.errors?.[0]?.message || response.error || 'Failed to update schedule';
          showSnackbar(errMsg, 'error');
          return;
        }

        showSnackbar(
          editingExpired
            ? 'Schedule activated with the new end date'
            : (response.message || 'Schedule updated'),
          'success'
        );
        onCreated?.(response.data);
        onClose();
        return;
      }

      const response = await createSchedule({
        ...schedulePayload,
        testgroupId: group.id,
      });

      if (response.status !== STATUS.SUCCESS) {
        const errMsg = response.code === 'TRIAL_EXPIRED'
          ? 'Your trial period has expired. Upgrade your plan to create a schedule.'
          : (response.errors?.[0]?.message || response.error || 'Failed to create schedule');
        showSnackbar(errMsg, 'error');
        return;
      }

      const payload = response.data || {};
      const schedule = payload.schedule || payload;
      const runOnceResult = payload.runOnceResult;

      if (runOnceResult && !runOnceResult.success) {
        showSnackbar(
          runOnceResult.error || 'Schedule was saved, but the immediate run could not start.',
          'warning'
        );
      } else {
        showSnackbar(
          form.runOnceNow && runOnceResult?.success
            ? (response.message || 'Schedule created and a run started now')
            : (response.message || 'Schedule created'),
          'success'
        );
        if (runOnceResult?.success) {
          window.dispatchEvent(new CustomEvent('trialUsageRefresh'));
        }
      }
      onCreated?.(schedule, {
        runOnceNow: Boolean(form.runOnceNow),
        runOnceSucceeded: Boolean(runOnceResult?.success),
      });
      onClose();
    } catch (error) {
      const errMsg = error.errors?.[0]?.message || error.message || 'Failed to create schedule';
      showSnackbar(errMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const footerButtons = [
    {
      label: 'Cancel',
      variant: 'outline',
      onClick: onClose,
      disabled: loading,
    },
    {
      label: isEditMode ? 'Save changes' : 'Save schedule',
      variant: 'primary',
      className: 'white bg-blue600',
      onClick: handleSubmit,
      disabled: loading || fetchingTestScope || fetchingSchedule
        || includedCaseCount(form.launchType, testScope) === 0,
      loading: loading || fetchingSchedule,
    },
  ];

  const envOptions = environments.map((env) => ({ value: env.id, label: env.name }));
  const showAiModel = needsAiModel || form.launchType !== LaunchType.MANUAL;
  const modalTitle = isEditMode ? 'Edit schedule' : 'New schedule';
  const scheduleTimezone = form.timezone || browserTimezone();
  const minStartDate = todayDateString();
  const minEndDate = laterDateString(tomorrowDateString(), form.startDate);
  const firstRunPreview = describeFirstScheduledRun({
    cadence: form.cadence,
    startDate: form.startDate,
    runTime: form.runTime,
    recurrenceDay: form.recurrenceDay,
    alreadyFiredToday: isEditMode && alreadyFiredToday,
    labelPrefix: isEditMode ? 'Next run' : 'First run',
  });

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={modalTitle}
      size="medium"
      className={scheduleStyles.modalWider}
      bodyClassName={scheduleStyles.modalBody}
      footer={(
        <div className={scheduleStyles.footerBar}>
          {firstRunPreview ? (
            <p className={scheduleStyles.footerFirstRun}>{firstRunPreview}</p>
          ) : (
            <span />
          )}
          <CommonFooter buttons={footerButtons} />
        </div>
      )}
    >
      <div className={scheduleStyles.root}>
        {modalError && (
          <Alert severity="error" sx={{ mb: 0 }}>
            {modalError}
          </Alert>
        )}
        {editingExpired && !modalError && (
          <Alert severity="info" sx={{ mb: 0 }}>
            This schedule has expired. Set a new end date in the future, then save to activate it again.
          </Alert>
        )}

        <InputBox
          label="Schedule name"
          name="scheduleName"
          value={form.name}
          onChange={(e) => {
            const value = e.target.value;
            updateField('name', value);
            if (value.trim().length > 255) {
              setFieldErrors((prev) => ({ ...prev, name: 'Schedule name cannot exceed 255 characters.' }));
            }
          }}
          required
          autoFocus
          error={fieldErrors.name}
        />

        <div className={`${typeCardStyles.section} ${scheduleStyles.typeSection}`}>
          <p className={typeCardStyles.sectionTitle}>What should this run cover?</p>
          <div className={typeCardStyles.cardList} role="radiogroup" aria-label="Launch type">
          {TYPE_OPTIONS.map((opt) => {
            const count = testScope[opt.countKey] || 0;
            const disabled = fetchingTestScope || count === 0;
            const selected = form.launchType === opt.value;
            const Icon = opt.Icon;
            const className = [
              typeCardStyles.card,
              selected && !disabled ? typeCardStyles.selected : '',
              disabled ? typeCardStyles.disabled : '',
            ].filter(Boolean).join(' ');

            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                onClick={() => handleLaunchTypeSelect(opt.value, disabled)}
                className={className}
              >
                <Icon className={typeCardStyles.icon} />
                <div className={typeCardStyles.body}>
                  <p className={typeCardStyles.title}>{opt.title}</p>
                  <p className={typeCardStyles.description}>{opt.description(count)}</p>
                </div>
                <div className={typeCardStyles.trailing}>
                  {disabled ? (
                    <LaunchTypeBanIcon className={typeCardStyles.banIcon} />
                  ) : selected ? (
                    <LaunchTypeCheckIcon className={typeCardStyles.checkIcon} />
                  ) : null}
                </div>
              </button>
            );
          })}
          </div>
        </div>

        {form.launchType !== LaunchType.MANUAL &&
        <div className={scheduleStyles.sequentialRow}>
          <CheckboxComponent
            name="isSequential"
            label="Run Sequentially"
            tooltip="Sequential mode runs tests one after another in defined order"
            checked={Boolean(form.isSequential)}
            onChange={(e) => updateField('isSequential', e.target.checked)}
            disabled={loading || fetchingSchedule}
            className={scheduleStyles.inlineCheckbox}
          />

          {form.isSequential && (
            <CheckboxComponent
              name="continueOnFailure"
              label="Continue On Failure"
              checked={Boolean(form.continueOnFailure)}
              onChange={(e) => updateField('continueOnFailure', e.target.checked)}
              disabled={loading || fetchingSchedule}
              className={scheduleStyles.inlineCheckbox}
            />
          )}
        </div>}

        <div
          className={`${scheduleStyles.envAiGrid}${showAiModel ? '' : ` ${scheduleStyles.envAiGridSolo}`}`}
        >
          <Selectbox
            label="Environment"
            name="environment"
            value={form.environmentId}
            onChange={(e) => updateField('environmentId', e.target.value)}
            options={envOptions}
            required
            disabled={fetchingEnvironments}
            error={fieldErrors.environmentId}
            helperText={fetchingEnvironments ? 'Loading environments...' : undefined}
          />

          {showAiModel && (
            <div className={scheduleStyles.aiModelWrap}>
              <AIModelRuntimeSelector
                key={aiSelectorKey}
                className={scheduleStyles.aiModelSelector}
                featureKey={AI_FEATURE_KEYS.TEST_LAUNCH}
                trialOrgSkip={isTrial}
                canSelectAiModel={canSelectAiModel}
                projectId={projectId ?? group?.project_id}
                initialModel={aiModelInitial}
                required={needsAiModel}
                disabled={fetchingTestScope || !canSelectAiModel || isTrial}
                readOnly={!canSelectAiModel && !isTrial}
                label="AI Model"
                error={fieldErrors.aiModel}
                onModelChange={(model, meta = {}) => {
                  setFieldErrors((prev) => {
                    if (!prev.aiModel) return prev;
                    const next = { ...prev };
                    delete next.aiModel;
                    return next;
                  });
                  setAiModelSelection({ model: model || null, meta: meta || {} });
                }}
              />
            </div>
          )}
        </div>

        <div className={scheduleStyles.timingSection}>
          <p className={scheduleStyles.timingTitle}>When it runs</p>
          <div className={scheduleStyles.timingGrid}>
            <Selectbox
              label="Repeats"
              name="cadence"
              value={form.cadence}
              onChange={(e) => updateField('cadence', e.target.value)}
              options={CADENCE_OPTIONS}
              required
              disableClearable
            />
            <InputBox
              label="Run time"
              name="runTime"
              type="time"
              value={form.runTime}
              onChange={(e) => updateField('runTime', e.target.value)}
              required
              error={fieldErrors.runTime}
            />
          </div>

          {form.cadence === ScheduleCadence.WEEKLY && (
            <Selectbox
              label="Day of week"
              name="recurrenceDay"
              value={form.recurrenceDay}
              onChange={(e) => updateField('recurrenceDay', e.target.value)}
              options={WEEKDAY_OPTIONS}
              required
              disableClearable
              error={fieldErrors.recurrenceDay}
            />
          )}

          {form.cadence === ScheduleCadence.MONTHLY && (
            <Selectbox
              label="Day of month"
              name="recurrenceDay"
              value={clampMonthDay(form.recurrenceDay)}
              onChange={(e) => updateField('recurrenceDay', e.target.value)}
              options={MONTH_DAY_OPTIONS}
              required
              disableClearable
              error={fieldErrors.recurrenceDay}
              helperText="If the month has fewer days, the last day of that month is used."
            />
          )}

          <div className={scheduleStyles.datesGrid}>
            <InputBox
              label="Start date"
              name="startDate"
              type="date"
              value={form.startDate}
              onChange={(e) => updateField('startDate', e.target.value)}
              min={minStartDate}
              required
              error={fieldErrors.startDate}
            />
            <InputBox
              label="End date"
              name="endDate"
              type="date"
              value={form.endDate}
              onChange={(e) => updateField('endDate', e.target.value)}
              min={minEndDate}
              required
              error={fieldErrors.endDate}
            />
          </div>

          {!isEditMode && (
          <div className={scheduleStyles.runOnceNow}>
            <CheckboxComponent
              name="runOnceNow"
              label="Also run once now, to check the setup works"
              checked={Boolean(form.runOnceNow)}
              onChange={(e) => updateField('runOnceNow', e.target.checked)}
              disabled={loading}
              size="small"
            />
          </div>
          )}
        </div>

        <p className={scheduleStyles.scheduleHint}>
          Times are in {formatScheduleTimezoneLabel(scheduleTimezone)}, taken from your browser.
        </p>
      </div>
    </ModalWrapper>
  );
}

export default CreateScheduleModal;
